import React, { useState, useEffect } from 'react';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SMSCounterProps {
  className?: string;
}

interface CounterData {
  total: number;
  sinceReset: number;
  loading: boolean;
  error: string | null;
}

const SMSCounter: React.FC<SMSCounterProps> = ({ className = '' }) => {
  const [counterData, setCounterData] = useState<CounterData>({
    total: 0,
    sinceReset: 0,
    loading: true,
    error: null
  });
  const [isResetting, setIsResetting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const fetchSMSCount = async () => {
    try {
      setIsRefreshing(true);
      
      const { data, error } = await supabase.rpc('get_sms_count');
      
      if (error) throw error;
      
      setCounterData({
        total: data.total || 0,
        sinceReset: data.since_reset || 0,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching SMS count:', error);
      setCounterData(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la récupération du compteur SMS'
      }));
    } finally {
      setIsRefreshing(false);
    }
  };

  const resetCounter = async () => {
    try {
      setIsResetting(true);
      
      // Vérifier le mot de passe
      const { data: appSettings, error: settingsError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'delete_task_password')
        .single();

      if (settingsError) {
        throw settingsError;
      }

      if (appSettings.value.hash !== password) {
        setPasswordError('Mot de passe incorrect');
        setIsResetting(false);
        return;
      }
      
      const { data, error } = await supabase.rpc('reset_sms_counter');
      
      if (error) throw error;
      
      if (!data.success) {
        throw new Error(data.error || 'Erreur lors de la réinitialisation du compteur');
      }
      
      // Refresh the counter after reset
      await fetchSMSCount();
      
      // Close the password modal
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError(null);
      
    } catch (error) {
      console.error('Error resetting SMS counter:', error);
      setCounterData(prev => ({
        ...prev,
        error: 'Erreur lors de la réinitialisation du compteur'
      }));
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {
    fetchSMSCount();
    
    // Set up interval to refresh the counter every 5 minutes
    const intervalId = setInterval(fetchSMSCount, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Compteur SMS</h2>
        <div className="flex space-x-2">
          <button
            onClick={fetchSMSCount}
            disabled={isRefreshing}
            className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowPasswordModal(true)}
            disabled={isResetting}
            className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors"
            title="Réinitialiser le compteur"
          >
            <RotateCcw className={`h-4 w-4 ${isResetting ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {counterData.error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-3">
          {counterData.error}
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-blue-600 mb-1">Total</div>
          <div className="text-2xl font-bold text-blue-800">
            {counterData.loading ? (
              <div className="animate-pulse h-8 w-16 bg-blue-200 rounded"></div>
            ) : (
              counterData.total.toLocaleString()
            )}
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-green-600 mb-1">Depuis réinitialisation</div>
          <div className="text-2xl font-bold text-green-800">
            {counterData.loading ? (
              <div className="animate-pulse h-8 w-16 bg-green-200 rounded"></div>
            ) : (
              counterData.sinceReset.toLocaleString()
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-3 text-xs text-gray-500">
        Dernière actualisation: {new Date().toLocaleString('fr-FR')}
      </div>

      {/* Modal de confirmation avec mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Réinitialiser le compteur SMS</h2>
            
            <p className="mb-4 text-gray-700">
              Cette action va réinitialiser le compteur "Depuis réinitialisation" à zéro. 
              Veuillez entrer le mot de passe pour confirmer.
            </p>
            
            <div className="mb-4">
              <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Entrez le mot de passe"
              />
              {passwordError && (
                <p className="mt-2 text-sm text-red-600">{passwordError}</p>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                  setPasswordError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isResetting}
              >
                Annuler
              </button>
              
              <button
                type="button"
                onClick={resetCounter}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isResetting || !password}
              >
                {isResetting ? 'Réinitialisation...' : 'Réinitialiser'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSCounter;